import React, { useState, useEffect } from 'react';
import { apiBaseURL, dashboardAPI } from '../services/apiClient';
import authService from '../services/authService';

const ApiTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authInfo, setAuthInfo] = useState(null);

  // Get authentication info on component mount
  useEffect(() => {
    const info = {
      isAuthenticated: authService.isAuthenticated(),
      hasToken: !!authService.getToken(),
      userInfo: authService.getUserInfo(),
      userRoles: authService.getUserRoles(),
      hasRequiredAccess: authService.hasRequiredAccess(),
      tokenPreview: authService.getToken() ? `${authService.getToken().substring(0, 20)}...` : 'No token'
    };
    setAuthInfo(info);
  }, []);

  const runTests = async () => {
    setLoading(true);
    setTestResults([]);
    const results = [];

    // Test 1: Total Slips
    try {
      const response = await dashboardAPI.getTotalSlips();
      results.push({
        endpoint: '/overview/total-slips',
        status: 'SUCCESS',
        statusCode: response.status,
        data: response.data
      });
    } catch (error) {
      results.push({
        endpoint: '/overview/total-slips',
        status: 'FAILED',
        statusCode: error.response?.status || 'Network Error',
        error: error.response?.data || error.message
      });
    }

    // Test 2: Total Valid Slips
    try {
      const response = await dashboardAPI.getTotalValidSlips();
      results.push({
        endpoint: '/overview/total-valid-slips',
        status: 'SUCCESS',
        statusCode: response.status,
        data: response.data
      });
    } catch (error) {
      results.push({
        endpoint: '/overview/total-valid-slips',
        status: 'FAILED',
        statusCode: error.response?.status || 'Network Error',
        error: error.response?.data || error.message
      });
    }

    // Test 3: Total Hills Sales
    try {
      const response = await dashboardAPI.getTotalHillsSales();
      results.push({
        endpoint: '/overview/total-hills-sales',
        status: 'SUCCESS',
        statusCode: response.status,
        data: response.data
      });
    } catch (error) {
      results.push({
        endpoint: '/overview/total-hills-sales',
        status: 'FAILED',
        statusCode: error.response?.status || 'Network Error',
        error: error.response?.data || error.message
      });
    }

    setTestResults(results);
    setLoading(false);
  };

  const getStatusColor = (status) => {
    return status === 'SUCCESS' ? '#4CAF50' : '#f44336';
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>API Connection Test</h2>
      
      {/* Authentication Status */}
      {authInfo && (
        <div style={{
          backgroundColor: authInfo.isAuthenticated ? '#e8f5e9' : '#ffebee',
          border: `2px solid ${authInfo.isAuthenticated ? '#4CAF50' : '#f44336'}`,
          borderRadius: '8px',
          padding: '15px',
          marginBottom: '20px'
        }}>
          <h3 style={{ marginTop: 0 }}>🔐 Authentication Status</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <strong>Authenticated:</strong> {authInfo.isAuthenticated ? '✅ Yes' : '❌ No'}
            </div>
            <div>
              <strong>Has Token:</strong> {authInfo.hasToken ? '✅ Yes' : '❌ No'}
            </div>
            <div>
              <strong>Has Required Access:</strong> {authInfo.hasRequiredAccess ? '✅ Yes' : '❌ No'}
            </div>
            <div>
              <strong>User Roles:</strong> {authInfo.userRoles.length > 0 ? authInfo.userRoles.join(', ') : 'None'}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>Username:</strong> {authInfo.userInfo?.preferred_username || authInfo.userInfo?.name || 'Unknown'}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <strong>Token Preview:</strong> <code style={{ fontSize: '12px' }}>{authInfo.tokenPreview}</code>
            </div>
          </div>
          {!authInfo.isAuthenticated && (
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
              ⚠️ <strong>Warning:</strong> You are not authenticated. API calls will fail. Please log in first.
            </div>
          )}
          {authInfo.isAuthenticated && !authInfo.hasRequiredAccess && (
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
              ⚠️ <strong>Warning:</strong> You don't have the required roles (headoffice or admin). API calls may fail.
            </div>
          )}
        </div>
      )}

      {/* API Configuration */}
      <div style={{
        backgroundColor: '#e3f2fd',
        border: '2px solid #2196F3',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0 }}>⚙️ API Configuration</h3>
        <p><strong>API Base URL:</strong> <code>{apiBaseURL}</code></p>
        <p><strong>Keycloak URL:</strong> <code>{window._env_?.REACT_APP_KEYCLOAK_URL || 'https://sign-me-in.techsys.co.za'}</code></p>
        <p><strong>Keycloak Realm:</strong> <code>{window._env_?.REACT_APP_KEYCLOAK_REALM || 'hills'}</code></p>
        <p><strong>Client ID:</strong> <code>{window._env_?.REACT_APP_KEYCLOAK_CLIENT_ID || 'coupon-insights-ui'}</code></p>
      </div>
      
      <button 
        onClick={runTests} 
        disabled={loading}
        style={{
          padding: '10px 20px',
          backgroundColor: loading ? '#ccc' : '#2196F3',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          marginBottom: '20px'
        }}
      >
        {loading ? 'Testing...' : 'Run API Tests'}
      </button>

      {testResults.length > 0 && (
        <div>
          <h3>Test Results:</h3>
          {testResults.map((result, index) => (
            <div 
              key={index}
              style={{
                border: `2px solid ${getStatusColor(result.status)}`,
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px',
                backgroundColor: '#f9f9f9'
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '10px'
              }}>
                <h4 style={{ margin: 0 }}>{result.endpoint}</h4>
                <span style={{
                  padding: '5px 15px',
                  borderRadius: '20px',
                  backgroundColor: getStatusColor(result.status),
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  {result.status}
                </span>
              </div>
              
              <p style={{ margin: '5px 0' }}>
                <strong>Status Code:</strong> {result.statusCode}
              </p>
              
              {result.status === 'SUCCESS' ? (
                <div>
                  <strong>Response Data:</strong>
                  <pre style={{
                    backgroundColor: '#e8f5e9',
                    padding: '10px',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                </div>
              ) : (
                <div>
                  <strong>Error Details:</strong>
                  <div style={{ marginTop: '10px' }}>
                    {result.statusCode === 401 && (
                      <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '10px' }}>
                        🔒 <strong>401 Unauthorized:</strong> Authentication failed. Check if your token is valid or if you need to log in again.
                      </div>
                    )}
                    {result.statusCode === 403 && (
                      <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '10px' }}>
                        🚫 <strong>403 Forbidden:</strong> You don't have permission to access this resource. Check your user roles.
                      </div>
                    )}
                    {result.statusCode === 'Network Error' && (
                      <div style={{ padding: '10px', backgroundColor: '#fff3cd', borderRadius: '4px', marginBottom: '10px' }}>
                        🌐 <strong>Network Error:</strong> Cannot reach the API server. Check your internet connection or CORS settings.
                      </div>
                    )}
                    <pre style={{
                      backgroundColor: '#ffebee',
                      padding: '10px',
                      borderRadius: '4px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {JSON.stringify(result.error, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ApiTest;

